import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { getMonthOptions, currentMonth, getDateRangeForMonth, formatNum } from '../../lib/month';
import { useMdr } from '../../hooks/useMdr';

interface PayoutLine {
  subMdrId: string;
  subMdrName: string;
  payinAmount: number;
  payoutAmount: number;
  status: string;
}
interface PayoutItem {
  id: string;
  period: string;
  startDate: string;
  endDate: string;
  totalPayinAmount: number;
  totalPayoutAmount: number;
  payinSharingAmount: number;
  payoutSharingAmount: number;
  status: 'unpaid' | 'paid';
  paymentMethod: string | null;
  lines: PayoutLine[];
}

export function MdrPayouts() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isMaster } = useMdr();
  const [month, setMonth] = useState(currentMonth());
  const monthOptions = getMonthOptions();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['mdr', 'payouts'],
    queryFn: () => apiGet<{ items: PayoutItem[]; total: number }>('/mdr/payouts'),
  });

  const generateMutation = useMutation({
    mutationFn: () => {
      const { dateFrom, dateTo } = getDateRangeForMonth(month);
      return apiPost<PayoutItem>('/mdr/payouts', {
        period: 'monthly',
        startDate: dateFrom,
        endDate: dateTo,
      });
    },
    onSuccess: (res) => {
      if (res.ok) {
        setError(null);
        queryClient.invalidateQueries({ queryKey: ['mdr', 'payouts'] });
      } else {
        setError(res.error.message);
      }
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: (id: string) =>
      apiPost(`/mdr/payouts/${id}/mark-paid`, { paymentMethod: 'transfer' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mdr', 'payouts'] }),
  });

  const items = data?.ok ? data.data.items : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {t('mdr.payoutsTitle')}
        </h1>
        {isMaster && (
          <div className="flex items-center gap-3">
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
            >
              {t('mdr.generatePayout')}
            </Button>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {isLoading && <div className="text-gray-400">{t('common.loading')}</div>}
      {!isLoading && items.length === 0 && (
        <div className="text-gray-400">{t('common.noData')}</div>
      )}

      <div className="space-y-4">
        {items.map((p) => (
          <Card key={p.id}>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle>
                  {p.startDate.slice(0, 10)} ~ {p.endDate.slice(0, 10)}{' '}
                  <Badge variant={p.status === 'paid' ? 'success' : 'warning'}>
                    {t(`payouts.${p.status}`)}
                  </Badge>
                </CardTitle>
                {isMaster && p.status === 'unpaid' && (
                  <Button size="sm" variant="outline" onClick={() => markPaidMutation.mutate(p.id)}>
                    {t('payouts.markPaid')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div>
                  <div className="text-xs text-gray-500">{t('mdr.totalPayin')}</div>
                  <div className="font-bold">{formatNum(p.totalPayinAmount)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">{t('mdr.totalPayout')}</div>
                  <div className="font-bold">{formatNum(p.totalPayoutAmount)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">{t('mdr.sharedPayin')}</div>
                  <div className="font-bold text-blue-600">{formatNum(p.payinSharingAmount)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">{t('mdr.sharedPayout')}</div>
                  <div className="font-bold text-green-600">
                    {formatNum(p.payoutSharingAmount)}
                  </div>
                </div>
              </div>

              {p.lines.length > 0 && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('mdr.subMdr')}</TableHead>
                        <TableHead className="text-right">{t('transactions.calcPayinFee')}</TableHead>
                        <TableHead className="text-right">
                          {t('transactions.calcPayoutFee')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {p.lines.map((l) => (
                        <TableRow key={l.subMdrId}>
                          <TableCell>{l.subMdrName}</TableCell>
                          <TableCell className="text-right text-blue-600">
                            {formatNum(l.payinAmount)}
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            {formatNum(l.payoutAmount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
